'use client';

import { useCallback } from 'react';
import { useGetPacAffiliationsV2Query } from 'src/api/redux';
import { getNewExport } from 'src/api';
import { useTableFilterSelections } from '../filters';
import { getSearchQueryParamsForAPI } from 'src/utils/url.helpers';

interface TableParams {
  sort?: string;
  sort_id?: string | null;
  sort_field?: string | null;
  order?: string;
  page?: number;
  pageSize?: number;
  q?: string | null;
}

interface UseGetPacAffiliationsV2Params {
  pacId: string;
  tableParams: TableParams;
}

const useGetPacAffiliationsV2 = ({
  pacId,
  tableParams,
}: UseGetPacAffiliationsV2Params) => {
  const { apiFilterFormat, apiVolumeFilterFormat } = useTableFilterSelections('affiliations');

  // Build the data object for the POST body
  const data = {
    ...apiVolumeFilterFormat,
  };

  // Get filter set IDs
  const filterSets = apiFilterFormat;

  const { data: responseData, isLoading, isFetching, error } = useGetPacAffiliationsV2Query(
    {
      pacId,
      tableParams,
      filterSets,
      data,
    },
    {
      skip: !pacId,
    }
  );

  // Build query params for export (matches original implementation)
  const queryParams = getSearchQueryParamsForAPI(tableParams);
  const filterSetQueryParams = filterSets
    .map(
      (id) => `&${encodeURIComponent('filter_set')}=${encodeURIComponent(String(id))}`
    )
    .join('');
  const fullQueryParams = queryParams + filterSetQueryParams;

  const exportAffiliations = useCallback(
    async ({ fileFormat, columns }: {
      fileFormat: string;
      columns: string[];
    }) => {
      const url = `https://api.medscout.io/api/v1/entities/v2/hcp-group/${pacId}/affiliations/${fullQueryParams}`;

      try {
        await getNewExport({
          method: 'POST',
          url,
          fileFormat,
          columns,
          data,
        });
      } catch (error) {
        console.error('Export error:', error);
        throw error;
      }
    },
    [pacId, fullQueryParams, data]
  );

  return {
    data: responseData,
    isLoading,
    isFetching,
    error,
    exportAffiliations,
    isValidating: isFetching,
  };
};

export default useGetPacAffiliationsV2;
